import { DashedTag } from '@praetorian-chariot/ui';
import React from 'react';

import { Accordion, AccordionItem } from '@/components/Accordion';
import { CopyToClipboard } from '@/components/CopyToClipboard';

/**
 * Prerequisites Accordion Component
 *
 * Displays setup prerequisites for Aegis Agent installation including:
 * - Admin rights requirements
 * - Hardware virtualization setup
 * - VMWare deployment notes
 * - AV/EDR whitelist configurations
 * - Network/firewall requirements
 */
export const PrerequisitesAccordion: React.FC = () => {
  const prerequisitesAccordionItems: AccordionItem[] = [
    {
      id: 'getting-started',
      title: '1. Getting Started',
      subtitle: 'Initial Setup and Permissions',
      defaultOpen: true,
      content: (
        <div className="space-y-3">
          <div className="border-chariot-stroke border-b pb-4">
            <h3 className="text-chariot-text-primary text-lg font-semibold">
              Admin Rights
            </h3>
            <p className="text-chariot-text-secondary">
              Ensure you have admin privileges on all target endpoints.
            </p>
          </div>
          <div className="border-chariot-stroke border-b pb-4">
            <h3 className="text-chariot-text-primary text-lg font-semibold">
              Hardware Virtualization
            </h3>
            <p className="text-chariot-text-secondary">
              Recommended but not required. Enable in BIOS or your
              virtualization platform (e.g., vSphere) for enhanced performance.
            </p>
          </div>
          <div className="pb-4">
            <h3 className="text-chariot-text-primary text-lg font-semibold">
              VMWare Deployment Notes
            </h3>
            <p className="text-chariot-text-secondary">
              Recommended but not required. Enable in BIOS or your
              virtualization platform (e.g., vSphere) for enhanced performance.
            </p>
            <ul className="text-chariot-text-secondary mt-2 list-inside list-disc space-y-2 text-sm">
              <li>
                <strong>Port Security:</strong> Disable or properly cnfigure
                port security on the virtual switch.
              </li>
              <li>
                <strong>Promiscuous Mode:</strong> Enable on the vSwitch port
                group where the parent ESXi VM is connected.
              </li>
              <li>
                <strong>Forged Transmits:</strong> Also enable on the same port
                group to allow traffic from nested VMs.
              </li>
              <li>
                <strong>Alternative (vSphere 7.0+):</strong> Instead of the
                above, you can enable MAC Learning on the port group to permit
                traffic from nested VM MAC addresses.
              </li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      id: 'antivirus-edr',
      title: '2. Anti Virus/EDR Considerations',
      subtitle: 'White Listing and Exclusions',
      content: (
        <div className="space-y-3">
          <p>
            Whitelist the following directories to prevent AV/EDR interference:
          </p>
          <div className="overflow-x-auto rounded-md">
            <table className="min-w-full text-left text-sm">
              <thead className="border-chariot-stroke border-b-2">
                <tr>
                  <th className="text-chariot-text-primary px-6 py-3 font-semibold">
                    Directory Type
                  </th>
                  <th className="text-chariot-text-primary px-6 py-3 font-semibold">
                    Windows Path
                  </th>
                  <th className="text-chariot-text-primary px-6 py-3 font-semibold">
                    Linux Path
                  </th>
                </tr>
              </thead>
              <tbody className="divide-chariot-stroke divide-y">
                <tr>
                  <td className="text-chariot-text-primary px-6 py-4 font-medium">
                    Aegis Installation Directory
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div className="bg-chariot-background-secondary text-chariot-text-secondary rounded px-3 py-1 font-mono text-xs">
                        <CopyToClipboard
                          textToCopy={'C:\\ProgramData\\Praetorian Aegis'}
                          alwaysShowCopyIcon
                        >
                          <span className="pr-2">
                            C:\ProgramData\Praetorian Aegis
                          </span>
                        </CopyToClipboard>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div className="bg-chariot-background-secondary text-chariot-text-secondary rounded px-3 py-1 font-mono text-xs">
                        <CopyToClipboard
                          textToCopy="/opt/Aegis"
                          alwaysShowCopyIcon
                        >
                          <span className="pr-2">/opt/Aegis</span>
                        </CopyToClipboard>
                      </div>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="text-chariot-text-primary px-6 py-4 font-medium">
                    Temporary Staging Directory
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div className="bg-chariot-background-secondary text-chariot-text-secondary rounded px-3 py-1 font-mono text-xs">
                        <CopyToClipboard
                          textToCopy={
                            'C:\\ProgramData\\Praetorian Aegis\\tools'
                          }
                          alwaysShowCopyIcon
                        >
                          <span className="pr-2">
                            C:\ProgramData\Praetorian Aegis\tools
                          </span>
                        </CopyToClipboard>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div className="bg-chariot-background-secondary text-chariot-text-secondary rounded px-3 py-1 font-mono text-xs">
                        <CopyToClipboard
                          textToCopy="/var/cache/Aegis"
                          alwaysShowCopyIcon
                        >
                          <span className="pr-2">/var/cache/Aegis</span>
                        </CopyToClipboard>
                      </div>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="text-chariot-text-primary px-6 py-4 font-medium">
                    Artifact Cache/Download Directory
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div className="bg-chariot-background-secondary text-chariot-text-secondary rounded px-3 py-1 font-mono text-xs">
                        <CopyToClipboard
                          textToCopy={
                            'C:\\ProgramData\\Praetorian Aegis\\cache'
                          }
                          alwaysShowCopyIcon
                        >
                          <span className="pr-2">
                            C:\ProgramData\Praetorian Aegis\cache
                          </span>
                        </CopyToClipboard>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div className="bg-chariot-background-secondary text-chariot-text-secondary rounded px-3 py-1 font-mono text-xs">
                        <CopyToClipboard
                          textToCopy="/var/cache/Aegis"
                          alwaysShowCopyIcon
                        >
                          <span className="pr-2">/var/cache/Aegis</span>
                        </CopyToClipboard>
                      </div>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="text-chariot-text-primary px-6 py-4 font-medium">
                    Logs Directory
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div className="bg-chariot-background-secondary text-chariot-text-secondary rounded px-3 py-1 font-mono text-xs">
                        <CopyToClipboard
                          textToCopy={'C:\\ProgramData\\Praetorian Aegis\\logs'}
                          alwaysShowCopyIcon
                        >
                          <span className="pr-2">
                            C:\ProgramData\Praetorian Aegis\logs
                          </span>
                        </CopyToClipboard>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div className="bg-chariot-background-secondary text-chariot-text-secondary rounded px-3 py-1 font-mono text-xs">
                        <CopyToClipboard
                          textToCopy="/var/log/Aegis"
                          alwaysShowCopyIcon
                        >
                          <span className="pr-2">/var/log/Aegis</span>
                        </CopyToClipboard>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ),
    },
    {
      id: 'network-requirements',
      title: '3. Network Requirements/Firewall Settings',
      subtitle: 'Connectivity and Firewall Settings',
      content: (
        <div className="space-y-3">
          <div className="space-y-2">
            <p>Allow outbound connections to the following services.</p>
            <div className="overflow-x-auto rounded-md">
              <table className="min-w-full text-left text-sm">
                <thead className="border-chariot-stroke border-b-2">
                  <tr>
                    <th className="text-chariot-text-primary px-6 py-3 font-semibold">
                      Hostname
                    </th>
                    <th className="text-chariot-text-primary px-6 py-3 font-semibold">
                      Port
                    </th>
                    <th className="text-chariot-text-primary px-6 py-3 font-semibold">
                      Protocol
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-chariot-stroke divide-y">
                  <tr>
                    <td className="text-chariot-text-primary px-6 py-4 font-medium">
                      agent.chariot.praetorian.com
                    </td>
                    <td className="px-6 py-4">
                      <DashedTag
                        label="443"
                        size="sm"
                        className="border-chariot-stroke text-chariot-text-secondary rounded-full"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <DashedTag
                        label="TCP"
                        size="sm"
                        className="border-chariot-stroke text-chariot-text-secondary rounded-full"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="text-chariot-text-primary px-6 py-4 font-medium">
                      region1.v2.argotunnel.com
                    </td>
                    <td className="px-6 py-4">
                      <DashedTag
                        label="7844"
                        size="sm"
                        className="border-chariot-stroke text-chariot-text-secondary rounded-full"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <DashedTag
                        label="TCP"
                        size="sm"
                        className="border-chariot-stroke text-chariot-text-secondary rounded-full"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="text-chariot-text-primary px-6 py-4 font-medium">
                      region2.v2.argotunnel.com
                    </td>
                    <td className="px-6 py-4">
                      <DashedTag
                        label="7844"
                        size="sm"
                        className="border-chariot-stroke text-chariot-text-secondary rounded-full"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <DashedTag
                        label="UDP"
                        size="sm"
                        className="border-chariot-stroke text-chariot-text-secondary rounded-full"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="text-chariot-text-primary px-6 py-4 font-medium">
                      api.cloudflare.com
                    </td>
                    <td className="px-6 py-4">
                      <DashedTag
                        label="443"
                        size="sm"
                        className="border-chariot-stroke text-chariot-text-secondary rounded-full"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <DashedTag
                        label="TCP"
                        size="sm"
                        className="border-chariot-stroke text-chariot-text-secondary rounded-full"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="text-chariot-text-primary px-6 py-4 font-medium">
                      1.1.1.1
                    </td>
                    <td className="px-6 py-4">
                      <DashedTag
                        label="853"
                        size="sm"
                        className="border-chariot-stroke text-chariot-text-secondary rounded-full"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <DashedTag
                        label="TCP"
                        size="sm"
                        className="border-chariot-stroke text-chariot-text-secondary rounded-full"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className="text-chariot-text-primary px-6 py-4 font-medium">
                      1.1.1.1
                    </td>
                    <td className="px-6 py-4">
                      <DashedTag
                        label="53"
                        size="sm"
                        className="border-chariot-stroke text-chariot-text-secondary rounded-full"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <DashedTag
                        label="UDP"
                        size="sm"
                        className="border-chariot-stroke text-chariot-text-secondary rounded-full"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ),
    },
  ];

  return <Accordion items={prerequisitesAccordionItems} />;
};
